import { Component, Input, OnInit } from '@angular/core';
import {
  ModalController,
  NavController,
  Platform,
  IonicModule,
} from '@ionic/angular';
import { AirportListComponent } from 'src/app/modules/airport/airport-list/airport-list.component';
import { CountryCode } from 'src/app/models/country-code';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { SmartDatePipe } from 'src/app/pipes/smart-date.pipe';
import { TranslateService } from '@ngx-translate/core';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { FlightHotelComponent } from 'src/app/modules/shared/modals/flight-hotel/flight-hotel.component';
import { FlightBookingHelperService } from 'src/app/services/flight-booking-helper/flight-booking-helper.service';
import { AirlinesModalComponent } from '../../booking/flights-v2/shared-components/airlines-modal/airlines-modal.component';

interface FlightQueryInput {
  Origin: string;
  Destination: string;
  PreferredDepartureTime: string;
  PreferredArrivalTime: string;
  OriginCountry?: string;
  DestinationCountry?: string;
}

interface BookingQuery {
  flightQuery: FlightQueryInput[];
  travellerCount: any;
  travelClass: string;
  airlinePreference: any[];
  journeyType: string;
}

@Component({
  selector: 'app-flight-search',
  templateUrl: './flight-search.component.html',
  styleUrls: ['./flight-search.component.scss'],
})
export class FlightSearchComponent implements OnInit {
  @Input() fareType: any;
  @Input() isSearchDisabled: boolean = false;
  public radioState: string | undefined;
  public passenger: any = {};
  public selectedCountryCode: CountryCode;
  public airlineText: string;
  public storedSearchQuery: BookingQuery;
  bookFlightSearchForm: UntypedFormGroup;
  public sourceDestinationSameError = false;
  public depDate: any;
  public flightQueryFields: FlightQueryInput[] = [
    {
      Origin: '',
      Destination: '',
      PreferredDepartureTime: '',
      PreferredArrivalTime: '',
    },
    {
      Origin: '',
      Destination: '',
      PreferredDepartureTime: '',
      PreferredArrivalTime: '',
    },
  ];
  public bookingQuery: BookingQuery = {
    flightQuery: this.flightQueryFields,
    travellerCount: this.passenger,
    travelClass: 'AllClass',
    airlinePreference: [],
    journeyType: '',
  };
  public todayDate: string;
  public currentHeaderData: any = {
    prevPageLink: '/bajaj/travel?type=flights',
    title: 'flight.flights_search.header',
  };

  constructor(
    private flightBookingHelperService: FlightBookingHelperService,
    private modalController: ModalController,
    private navController: NavController,
    private formBuilder: UntypedFormBuilder,
    private smartDatePipe: SmartDatePipe,
    private translate: TranslateService,
    private analyticsService: AnalyticsService,
    private platform: Platform,
  ) {
    const today = new Date();
    this.todayDate = today.toISOString().substring(0, 10);
  }

  ngOnInit() {
    this.radioState = 'one_way';
    this.airlineText = '';
    this.passenger = {
      adult: 1,
      child: 0,
      infant: 0,
      travelClass: 'Economy',
    };
    this.loadStoredSearchQuery();
    this.handleSearchFormValidation();
    this.passengerDataHandler(this.passenger);
  }

  ionViewDidEnter() {
    this.loadStoredSearchQuery();
    this.handleSearchFormValidation();
    this.showFirstTimeModalIfNeeded();
  }

  private loadStoredSearchQuery() {
    this.storedSearchQuery = this.flightBookingHelperService
      .getSearchQuery as BookingQuery;
    if (this.storedSearchQuery) {
      switch (this.storedSearchQuery.journeyType) {
        case 'Oneway':
          this.radioState = 'one_way';
          break;
        case 'Return':
          this.radioState = 'return';
          break;
        case 'MultiCity':
          this.radioState = 'multi_city';
          break;
        default:
          this.radioState = 'one_way';
      }

      this.airlineText =
        this.storedSearchQuery?.airlinePreference?.length === 0
          ? ''
          : this.storedSearchQuery?.airlinePreference?.length === 1
          ? this.storedSearchQuery?.airlinePreference?.[0]?.airlineName
          : `${this.storedSearchQuery?.airlinePreference?.length} Airlines`;

      if (this.storedSearchQuery.travellerCount) {
        this.passenger = this.storedSearchQuery.travellerCount;
      }
    }
  }

  handleSearchFormValidation() {
    if (this.storedSearchQuery) {
      this.bookingQuery.airlinePreference =
        this.storedSearchQuery?.airlinePreference || [];
      this.bookingQuery.travelClass =
        this.storedSearchQuery?.travelClass || 'Economy';
      this.flightQueryFields = this.storedSearchQuery
        ?.flightQuery as FlightQueryInput[];
      if (this.flightQueryFields.length < 2) {
        this.flightQueryFields.push({
          Origin: '',
          Destination: '',
          PreferredDepartureTime: '',
          PreferredArrivalTime: '',
        });
      }
      if (this.storedSearchQuery.travellerCount) {
        this.passenger = this.storedSearchQuery.travellerCount;
      }
    }
    const formControls = {
      travellerCount: [
        // eslint-disable-next-line max-len
        `${this.passenger.adult} ${this.translate.instant(
          'flight.flights_search.adult'
        )}, ${this.passenger.child} ${this.translate.instant(
          'flight.flights_search.child'
        )}, ${this.passenger.infant} ${this.translate.instant(
          'flight.flights_search.infant'
        )} . ${this.bookingQuery.travelClass}`,
        Validators.compose([
          Validators.required,
          (control) => {
            const passengerCountString = control.value;
            const passengerCountArray = passengerCountString?.split(' ');
            const passenger = passengerCountArray?.length > 0 && {
              adult: parseInt(passengerCountArray[0]),
              child: parseInt(passengerCountArray[2]),
              infant: parseInt(passengerCountArray[4]),
            };
            if (passenger && passenger.adult > 0) {
              return null; // valid
            } else {
              return { required: true }; // invalid
            }
          },
        ]),
      ],

      travelClass: [this.bookingQuery?.travelClass],
      airlinePreference: [this.bookingQuery?.airlinePreference],
    };

    const loopingCondition =
      this.radioState === 'multi_city' ? this.flightQueryFields?.length : 1;
    for (let i = 0; i < loopingCondition; i++) {
      formControls[`source-${i}`] = [
        this.flightQueryFields?.[i]?.Origin,
        Validators.required,
      ];
      formControls[`destination-${i}`] = [
        this.flightQueryFields?.[i]?.Destination,
        Validators.required,
      ];
      formControls[`departureDate-${i}`] = [
        this.flightQueryFields?.[i]?.PreferredDepartureTime,
        Validators.required,
      ];
    }

    // eslint-disable-next-line @typescript-eslint/dot-notation
    formControls['returnDate'] = [
      this.flightQueryFields?.[1]?.PreferredDepartureTime || '',
      this.radioState === 'return' ? Validators.required : null,
    ];

    this.bookFlightSearchForm = this.formBuilder.group(formControls);
  }

  onRadioChange(event: any) {
    this.radioState = event.detail.value || this.radioState;
    this.handleSearchFormValidation();
  }

  onTabClick(tabValue: string) {
    this.radioState = tabValue;
    this.handleSearchFormValidation();
  }

  // Below Function is to Add Extra flight Field
  handleAddMoreFlight() {
    this.flightQueryFields.push({
      Origin: '',
      Destination: '',
      PreferredDepartureTime: '',
      PreferredArrivalTime: '',
    });
    this.handleSearchFormValidation();
  }
  // Below Function is to Remove a flight Field
  handleRemoveFlight(index) {
    this.flightQueryFields.splice(index, 1);
    this.handleSearchFormValidation();
  }

  //Below Function is generates the modal of airport list
  async getAirport(type: 'source' | 'destination', i?: number) {
    const fare = (this.fareType || '').toLowerCase();
    const isFlexi = fare === 'flexicancel' || fare === 'flexi_cancel';
    const modal = await this.modalController.create({
      component: AirportListComponent,
      componentProps: { type: 'flightbooking', fareType: isFlexi ? 'flexi_cancel' : '' },
      backdropDismiss: false,
    });
    modal.onDidDismiss().then(async (data) => {
      if (data.data) {
        if (type === 'source') {
          this.flightQueryFields[i].Origin = data.data.airport.airportCode;
          this.flightQueryFields[i].OriginCountry = data.data.airport.country;
          this.bookFlightSearchForm
            .get(`source-${i}`)
            ?.setValue(data.data.airport.airportCode);
        }
        if (type === 'destination') {
          this.flightQueryFields[i].Destination = data.data.airport.airportCode;
          this.flightQueryFields[i].DestinationCountry = data.data.airport.country;
          this.bookFlightSearchForm
            .get(`destination-${i}`)
            ?.setValue(data.data.airport.airportCode);
        }
        this.isSearchDisabled = false;
      } else {
        if (type === 'source' && !this.flightQueryFields[i]?.Origin) {
          this.bookFlightSearchForm.get(`source-${i}`)?.markAsTouched();
          this.bookFlightSearchForm
            .get(`source-${i}`)
            ?.updateValueAndValidity();
        }

        if (type === 'destination' && !this.flightQueryFields[i]?.Destination) {
          this.bookFlightSearchForm.get(`destination-${i}`)?.markAsTouched();
          this.bookFlightSearchForm
            .get(`destination-${i}`)
            ?.updateValueAndValidity();
        }
      }
      this.analyticsService.trackAirportEvent(
        `Selected Airport for ${this.fareType === 'flexicancel' ? 'FlexiCancel Flight' : 'Flight'}`,
        type,
        data?.data?.airport?.airportCode
      );
    });
    return await modal.present();
  }

  //switch source and destination
  switchSourceDestination() {
    if (
      this.flightQueryFields[0].Origin &&
      this.flightQueryFields[0].Destination
    ) {
      // eslint-disable-next-line max-len
      [
        this.flightQueryFields[0].Origin,
        this.flightQueryFields[0].Destination,
        this.flightQueryFields[0].OriginCountry,
        this.flightQueryFields[0].DestinationCountry,
      ] = [
        this.flightQueryFields[0].Destination,
        this.flightQueryFields[0].Origin,
        this.flightQueryFields[0].DestinationCountry,
        this.flightQueryFields[0].OriginCountry,
      ];
      // Reset search disabled state when airports are switched
      this.isSearchDisabled = false;
    }
  }

  //This Function gets Passenger count emiited by the modal component
  passengerDataHandler(passenger) {
    this.passenger = passenger;
    if (passenger?.travelClass) {
      this.bookingQuery.travelClass = passenger?.travelClass;
    }
    // Handle singular/plural forms for display text
    const adultText =
      passenger?.adult < 2
        ? this.translate.instant('flight.flights_search.adult')
        : this.translate.instant('flight.flights_search.adults');

    const childText =
      passenger?.child < 2
        ? this.translate.instant('flight.flights_search.child')
        : this.translate.instant('flight.flights_search.children');

    const infantText =
      passenger?.infant < 2
        ? this.translate.instant('flight.flights_search.infant')
        : this.translate.instant('flight.flights_search.infants');

    this.bookFlightSearchForm
      .get('travellerCount')
      // eslint-disable-next-line max-len
      .setValue(
        `${passenger?.adult} ${adultText}, ${passenger?.child} ${childText}, ${
          passenger?.infant
        } ${infantText} . ${
          passenger?.travelClass
            ? `${passenger.travelClass}`
            : this.bookingQuery.travelClass
        }`
      );
  }

  handleBooking() {
    if (this.radioState === 'multi_city') {
      this.bookingQuery.journeyType = 'MultiCity';
      this.bookingQuery.flightQuery = this.flightQueryFields;
    }

    if (this.radioState === 'return') {
      this.bookingQuery.journeyType = 'Return';
      this.flightQueryFields[1] = {
        ...this.flightQueryFields[1],
        Origin: this.flightQueryFields[0].Destination,
        Destination: this.flightQueryFields[0].Origin,
      };
      this.bookingQuery.flightQuery = this.flightQueryFields;
    }

    if (this.radioState === 'one_way') {
      this.bookingQuery.journeyType = 'Oneway';
      this.bookingQuery.flightQuery = [this.flightQueryFields[0]];
    }

    this.bookingQuery.flightQuery = this.bookingQuery.flightQuery.map(
      (item) => {
        return { ...item, PreferredArrivalTime: item.PreferredDepartureTime };
      }
    );
    this.bookingQuery.travellerCount = this.passenger;
    this.bookingQuery.travelClass = this.bookingQuery.travelClass;

    this.flightBookingHelperService.saveSearchQuery = this.bookingQuery;

    const airlinePreference = JSON.stringify(
      this.bookingQuery.airlinePreference
    );

    this.bookingQuery.airlinePreference =
      this.bookingQuery.airlinePreference?.length > 0
        ? this.bookingQuery.airlinePreference.map(
            (item) => item?.airlineCode || item
          )
        : [];

    // creating url of bookingQuery Object
    const queryUrl = encodeURI(JSON.stringify(this.bookingQuery));
    const typeFromUrl = (typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('type')
      : '')
        ?.toLowerCase() || '';
    const targetRoute = typeFromUrl === 'flexicancel'
      ? 'flexicancel/v2/search-results'
      : 'flight-booking/v2/search-results';
    this.navController.navigateForward([targetRoute, { queryUrl: queryUrl }]);

    this.analyticsService.trackFlightSearchEvent({
      tripType: this.radioState === 'return' ? 'return' : 'one-way',
      departureCode: this.flightQueryFields?.[0]?.Origin,
      arrivalCode: this.flightQueryFields?.[0]?.Destination,
      departureDate: this.flightQueryFields?.[0]?.PreferredDepartureTime,
      returnDate:
        this.radioState === 'return'
          ? this.flightQueryFields?.[1]?.PreferredDepartureTime
          : null,
      adults: Number(this.passenger?.adult) || 0,
      children: Number(this.passenger?.child) || 0,
      infants: Number(this.passenger?.infant) || 0,
      cabinClass: this.bookingQuery?.travelClass || 'economy',
      airlinePreference: airlinePreference,
      queryUrl: queryUrl,
      flowType: typeFromUrl === 'flexicancel' ? 'FlexiCancel Flight' : 'Flight',
    });
  }

  handleTravelClassSelection(selectedClass) {
    this.bookingQuery.travelClass = selectedClass.target.value;
  }

  async getAirlines() {
    const isModalOpened = document.getElementById('airline');
    if (!isModalOpened) {
      const modalConfig: any = {
        component: AirlinesModalComponent,
        initialBreakpoint: 1,
        mode: 'ios',
        id: 'airline',
        componentProps: {
          airlinesData: this.bookingQuery?.airlinePreference || [],
        },
      };

      const modal = await this.modalController.create(modalConfig);

      modal.onDidDismiss().then(async (data) => {
        if (data.data) {
          this.bookingQuery.airlinePreference = data?.data;
        } else {
          this.bookingQuery.airlinePreference = [];
        }
        this.airlineText =
          this.bookingQuery.airlinePreference.length === 0
            ? ''
            : this.bookingQuery.airlinePreference.length === 1
            ? this.bookingQuery.airlinePreference[0].airlineName
            : `${this.bookingQuery.airlinePreference.length} Airlines`;
      });
      return await modal.present();
    }
  }

  removeSelectedAirlines(index: number) {
    this.bookingQuery.airlinePreference.splice(index, 1);
  }

  isSourceDestinationSame(i: number) {
    if (
      this.bookFlightSearchForm.get(`source-${i}`).value !== '' &&
      this.bookFlightSearchForm.get(`source-${i}`).value ===
        this.bookFlightSearchForm.get(`destination-${i}`).value
    ) {
      this.sourceDestinationSameError = true;
      return true;
    }
    this.sourceDestinationSameError = false;
    return false;
  }

  handleReturnDate() {
    const depDate = new Date(
      this.flightQueryFields?.[0].PreferredDepartureTime
    );
    const returnDate = new Date(
      this.flightQueryFields?.[1].PreferredDepartureTime
    );
    const today = new Date();
    if (
      returnDate.getTime() < depDate.getTime() ||
      returnDate.getTime() < today.getTime()
    ) {
      this.flightQueryFields[1].PreferredDepartureTime =
        this.smartDatePipe.transform(
          depDate.setDate(depDate.getDate()),
          'yyyy-MM-dd',
          'en-US'
        );
    }
  }

  handleDepDateMulticity(i: number) {
    const nextDepDate =
      new Date(this.flightQueryFields?.[i + 1]?.PreferredDepartureTime) ||
      new Date(9999, 11, 31);
    const prevDepDate = new Date(
      this.flightQueryFields?.[i]?.PreferredDepartureTime
    );
    const today = new Date();
    if (
      nextDepDate.getTime() < prevDepDate.getTime() ||
      nextDepDate.getTime() < today.getTime()
    ) {
      this.flightQueryFields[i + 1].PreferredDepartureTime =
        this.smartDatePipe.transform(
          prevDepDate.setDate(prevDepDate.getDate()),
          'yyyy-MM-dd',
          'en-US'
        );
    }
  }
  async showFlightHotelModal() {
    try {
      const modal = await this.modalController.create({
        component: FlightHotelComponent,
        cssClass: 'login-success-modal',
        backdropDismiss: true,
        showBackdrop: true,
        componentProps: { variant: 'flight' }
      });
      return await modal.present();
    } catch (error) {
      console.error('Error showing Flight Hotel Modal:', error);
    }
  }

  private async showFirstTimeModalIfNeeded() {
    try {
      // Check if modal has been shown before for flights
      const hasShownFlightModal = localStorage.getItem(
        'flightHotelModal_shown_flight'
      );
      const sessionKey = 'modal_shown_session_flight';

      if (hasShownFlightModal || sessionStorage.getItem(sessionKey)) {
        return;
      }

      await this.showFlightHotelModal();

      // Mark as shown in both localStorage and sessionStorage
      localStorage.setItem('flightHotelModal_shown_flight', 'true');
      sessionStorage.setItem(sessionKey, 'true');
    } catch (err) {
      console.error('Auto-show modal failed:', err);
    }
  }

  validateDomesticAirports(): boolean {
    const firstFlight = this.flightQueryFields[0];
    
    if (!firstFlight.Origin || !firstFlight.Destination) {
      return true; 
    }

    if (!firstFlight.OriginCountry || !firstFlight.DestinationCountry) {
      return true;
    }

    const isOriginDomestic = firstFlight.OriginCountry?.toLowerCase().trim() === 'india';
    
    const isDestinationDomestic = firstFlight.DestinationCountry?.toLowerCase().trim() === 'india';

    return isOriginDomestic && isDestinationDomestic;
  }
}
